import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import MUIDataTable from "mui-datatables";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Button, Grid } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {debounceSearchRender} from "mui-datatables";
import LinearProgress from "@mui/material/LinearProgress";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {similarityCompare} from "../../../utils/fuzzySearch";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../utils/FormFields/ConfirmDialog";
import { Employee, EmployeeApi } from "../../../api/clinic-management";
import { loginInfo, branchRoles, configState } from "../../../recoil/atoms";
import useAuthorization from "../../Authentication/permissions/Authorization";

type Props = {
  addButtonTitle: string;
  role: any;
};

export default function EmployeesTable({ addButtonTitle }: Props) {
  const config = useRecoilValue(configState);
  const loggedInBranch = useRecoilValue(loginInfo);
  const availableRoles = useRecoilValue(branchRoles);

  const [rowID, setRowID] = useState<string>("");
  const [employeeName, setEmployeeName] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const { canAddEmployeeUser, canGetEmployeesTenant, canEditEmployeeUser, canDeleteEmployeeTenant } = useAuthorization();

  const role = availableRoles?.find((item) => {
    return addButtonTitle === item.Name;
  });

  const getEmployees = () => {
    const employee = new EmployeeApi(config);
    if (role) {
      employee
        .getEmployeesofTenant(loggedInBranch?.BranchID, loggedInBranch?.TenantID, role?.RoleID)
        .then((res) => {          
          setEmployees(res.data);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      toast.error("These employees don't exist in this branch!");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteEmployee = (id: string) => {
    const employee = new EmployeeApi(config);
    employee
      .deleteTenantEmployee(id)
      .then((res) => {
        getEmployees();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deleteEmployee(rowID);
    handleCloseDialog();
  };
  const navigate = useNavigate();

  const columns = [
    {
      name: "EmployeeID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },
    {
      name: "User.FirstName",
      label: "First Name",
    },
    {
      name: "User.MiddleName",
      label: "Middle Name",
    },
    {
      name: "User.FamilyName",
      label: "Family Name",
    },
    {
      name: "User.Gender",
      label: "Gender",
    },
    { name: "Speciality", label: "Speciality", editable: true },
    {
      name: "Active",
      label: "Active",
      options: {
        filter: false,
        customBodyRender: () => ("Active" ? "Yes" : "No"),
      },
    },
    {
      name: "User.Contact.PrimaryEmail.Email",
      label: "Email",
    },
    {
      name: "User.Contact.PrimaryPhone.Phone",
      label: "Primary Phone",
    },
    {
      name: "User.Contact.SecondaryPhone.Phone",
      label: "Secondary Phone",
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            {canEditEmployeeUser && (
              <Tooltip title="Edit">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    navigate(
                      `/${addButtonTitle.toLowerCase()}/${tableMeta.tableData[tableMeta.rowIndex].EmployeeID}/edit`,
                      {
                        state: tableMeta.tableData[tableMeta.rowIndex],
                      }
                    );
                  }}
                >
                  <EditIcon color="secondary" />
                </Button>
              </Tooltip>
            )}
            {canDeleteEmployeeTenant && (
              <Tooltip title="Delete">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    const firstName = tableMeta.rowData[1];
                    const lastName = tableMeta.rowData[3];
                    const fullName = `${firstName} ${lastName}`;

                    handleDelete(tableMeta.rowData[0]);
                    setEmployeeName(fullName);
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </Button>
              </Tooltip>
            )}
          </>
        ),
      },
    },
  ];

  return (
    <>
      <MainCard>
        <Grid>
          {canGetEmployeesTenant && (
            <>
              {!isLoading ? (
                <>
                  <MUIDataTable
                    data={employees}
                    columns={columns}
                    options={{
                      filter: true,
                      viewColumns: false,
                      selectableRows: "none",
                      selectableRowsOnClick: false,
                      elevation: 0,
                      rowsPerPage: 10,
                      filterType: "dropdown",
                      enableNestedDataAccess: ".",
                      responsive: "standard",
                      customSearch: (searchQuery, currentRow, columns) => {
                        return similarityCompare(searchQuery, currentRow[1], currentRow[3], currentRow[8], );
                      },
                      customSearchRender: debounceSearchRender(400),
                      customToolbar: () => {
                        return (
                          canAddEmployeeUser && (
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<PersonAddIcon />}
                              onClick={() => {
                                navigate("/employee/add", {
                                  state: role,
                                });
                              }}
                              sx={{
                                ml: "50px",
                              }}
                            >
                              Add {addButtonTitle}
                            </Button>
                          )
                        );
                      },
                    }}
                    title=""
                  />
                  <ConfirmDialog
                    name={"employee"}
                    record={employeeName}
                    handleCloseDialog={handleCloseDialog}
                    handleConfirm={handleConfirm}
                    openDialog={openDialog}
                  />
                </>
              ) : (
                <LinearProgress color="secondary" />
              )}
            </>
          )}
        </Grid>
      </MainCard>
    </>
  );
}
